import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import { IoPrintOutline } from 'react-icons/io5';
import { MdOutlinePerson, MdOutlinePersonOff } from 'react-icons/md';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Spinner from 'assets/gif/loading.gif';
import axios from 'config/axios';
import Header from 'components/Header';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import CategoryDetailLabel from 'components/CategoryDetailLabel';
import Table from 'components/Table';
import Dropdown from 'components/Dropdown2';
import Loader from 'components/Loader';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';

import './index.css';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Dropdown4 from 'components/Dropdown4';
import QuestionPopup from 'components/QuestionPopup';

const timePerQuestionRW = 64 * 60 / 54;
const timePerQuestionMath = 70 * 60 / 44;
const CONFIGURATION= {
    'Reading and Writing': 'RW',
    'Math': 'M'
}

function ClassSATReport () {
    const [isQuestionPopupOpen, setIsQuestionPopupOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [questionId, setQuestionId] = useState(null);
    const [correctStudent, setCorrectStudent] = useState(0);
    const [currentQuestionData, setCurrentQuestionData] = useState(null);
    const [testType, setTestType] = useState('');
    const [scoreChartData, setScoreChartData] = useState<{ range: string; students: number }[]>([]);
    const [testData, setTestData] = useState(null);
    const [modules, setModules] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [subjectIndex, setSubjectIndex] = useState(0);
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const [printInProgress, setPrintInProgress] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [hideStudentNames, setHideStudentNames] = useState(false);

    const windowSize = useWindowSize();
    const { classId, testId } = useParams();
    const printRef = useRef(null);
    const questionPopupRef = useOutsideClickHandler(() => handleExit(false));
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const pathParts = location.pathname.split('/');
        const testType = pathParts[2]; // ex: 'digital-act' or 'digital-sat'
        setTestType(testType);
    }, [location]);

    const exportStudentTestData = () => {
        setDownloadInProgress(true);
        axios({
            url: `tutors/report/download/${classId}/${testId}/`,
            method: 'GET',
            responseType: 'blob',
        })
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${testData?.className}-${testData?.testName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadInProgress(false);
        })
        .catch(error => {
            console.error('Error downloading CSV:', error);
            setDownloadInProgress(false);
        });
    }

    const printBatchReport = () => {
        setPrintInProgress(true);
        axios.get(`tutors/report/print/${classId}/${testId}`)
            .then(res => {
                setReportData(res.data.studentTests);
                setPrintInProgress(false);
            })
            .catch(error => {
                console.log(error);
                setPrintInProgress(false);
            });

    }

    const handleQuestionPopup = (questionId: string) => {
        setQuestionId(questionId);
        axios.get(`tutors/classes/report/question/${questionId}/${testId}/`)
            .then(res => {
                setCurrentQuestionData(res.data)
            })
            .catch(error => {
                console.log(error);
            });

        setIsQuestionPopupOpen(true);
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current
    });

    const getScoreDistribution = (testSections, studentScores) => {
        let scoreRanges = [];
        const validScores = studentScores.filter(score => score !== null && score !== undefined);
    
        if (testSections === 2) {
            // SAT Total (400~1600)
            scoreRanges = [
                { label: '< 1100', min: 400, max: 1099 },
                { label: '1100 - 1199', min: 1100, max: 1199 },
                { label: '1200 - 1299', min: 1200, max: 1299 },
                { label: '1300 - 1399', min: 1300, max: 1399 },
                { label: '1400 - 1499', min: 1400, max: 1499 },
                { label: '1500 - 1599', min: 1500, max: 1599 },
                { label: "1600", min: 1600, max: 1600 }
            ];
        } else if (testSections=== 1) {
            // SAT Section (200~800)
            scoreRanges = [
                { label: '< 400', min: 200, max: 399 },
                { label: '400 - 499', min: 400, max: 499 },
                { label: '500 - 599', min: 500, max: 599 },
                { label: '600 - 699', min: 600, max: 699 },
                { label: '700 - 799', min: 700, max: 799 }, 
                { label: '800', min: 800, max: 800 },
            ];
        }
    
        // bins 초기화
        const bins = scoreRanges.map(range => ({
            range: range.label,
            students: 0,
        }));
    
        validScores.forEach(score => {
            for (let i = 0; i < scoreRanges.length; i++) {
                const { min, max } = scoreRanges[i];
                if (score >= min && score < max) {
                    bins[i].students += 1;
                    break;
                }
            }
        });
    
        return bins;
    };

    const handleExit = (isExit: boolean) => {
        setIsQuestionPopupOpen(isExit);
        setCurrentQuestionData(null);
    }

    useEffect(()=>{
        if (testData && questionId) {
            let correctCount = 0;
            let taken = 0;

            testData.students.forEach(student => {
                student.moduleAnswers.forEach(moduleAnswer => {
                    const answer = moduleAnswer.answers.find(a => a.questionId === questionId);
                    if (answer) {
                        taken +=1;
                        if (answer.isCorrect) {
                            correctCount += 1;
                        }
                    }
                });
            });

            setCorrectStudent(taken>0? (correctCount/taken)*100: null);
        }
    }, [testData, questionId]);

    useEffect(() => {
        if (!testType) return;

        axios.get(`tutors/classes/${classId}/${testType}/report/${testId}/`)
            .then(res => {
                setTestData(res.data);
                let modulesArray = [];
                let subjectsArray = [];
                for (let module of res.data.modules) {
                    modulesArray.push(`${module.subject} Module ${module.moduleNumber} ${module.difficulty ? module.difficulty : ''}`)
                }
                setModules(modulesArray);
                if (!res.data.isConfigured) {
                    subjectsArray.push('Total')
                }
                for (let subject of res.data.configTestSections) {
                    subjectsArray.push(subject)
                }
                setSubjects(subjectsArray);
                const firstSubject = subjectsArray[0];
                let data;
                if (firstSubject==='Total') {
                    data = getScoreDistribution(
                        2,
                        res.data.studentScores
                    );
                } else if (firstSubject==='Reading and Writing') {
                    data = getScoreDistribution(
                        1,
                        res.data.studentEnglishScores
                    );
                } else if (firstSubject==='Math') {
                    data = getScoreDistribution(
                        1,
                        res.data.studentMathScores
                    );
                }
                setScoreChartData(data)
            })
            .catch(error => {
                console.log(error);
            });
    }, [testType]);

    useEffect(()=>{
        const selected = subjects[subjectIndex]
        let data;
        if (selected==='Total') {
            data = getScoreDistribution(
                2,
                testData.studentScores
            );
        } else if (selected==='Reading and Writing') {
            data = getScoreDistribution(
                1,
                testData.studentEnglishScores
            );
        } else if (selected==='Math') {
            data = getScoreDistribution(
                1,
                testData.studentMathScores
            );
        }
        setScoreChartData(data)
    }, [subjectIndex]);

    useEffect(() => {
        if (reportData) {
            handlePrint();
        }
    }, [reportData]);

    if (testData === null) return <Loader />;

    return (
        <div className='classReport'>
            <Helmet>
                <title>
                    Class Report - Test Center Admin
                </title>
            </Helmet>
            {isQuestionPopupOpen &&
                <QuestionPopup
                    info={currentQuestionData?.info}
                    correctStudent={correctStudent!==null? `${correctStudent.toFixed(1)}%`: '-'}
                    testName={testData?.testName}
                    question={currentQuestionData?.question}
                    passages={currentQuestionData?.passages}
                    figures={currentQuestionData?.figures}
                    choices={currentQuestionData?.choices}
                    correctChoice={currentQuestionData?.correctChoice}
                    correctAnswers={currentQuestionData?.correctAnswers}
                    manageExit={() => handleExit(false)}
                    innerRef={questionPopupRef}
                />
            }
            <Header
                title={(testData?.testName && testData?.configTestSections?.length>0)? (testData?.configTestSections?.length==1?`${testData.testName} (${testData.configTestSections.map(section=> CONFIGURATION[section]).join(', ')})`:`${testData.testName}`) : null}
                prevRoutes={testData && [{ name: testData.className, path: `/classes/${classId}` }]}
            />
            <div className='classReport__subHeaderRow'>
                <h2 className='classReport__subHeader'>
                    Scores by students
                </h2>
                <div className='classReport__buttonGroup'>
                    <Button className='classReport__button' secondary onClick={() => setHideStudentNames(!hideStudentNames)}>
                        {hideStudentNames
                        ?   <>
                                <MdOutlinePerson className='classReport__buttonIcon' />
                                <p>Show</p>
                            </>
                        :   <>
                                <MdOutlinePersonOff className='classReport__buttonIcon' />
                                <p>Hide</p>
                            </>
                        }
                    </Button>
                    <Button className='classReport__button' onClick={printBatchReport}>
                        {printInProgress
                        ?   <img src={Spinner} />
                        :   <>
                                <IoPrintOutline className='classReport__buttonIcon' />
                                <p>Report</p>                            
                            </>
                        }
                    </Button>
                </div>
            </div>
            <div className='classReport__scoresContainer'>
                <Table
                    className='classReport__table'
                    header={[
                        'Students',
                        ...testData.classTestSections.map((classTestSection, p) => (
                            <p className='classReport__cellCenter'>
                                {classTestSection}
                            </p>
                        )),
                        <p className='classReport__cellCenter'>Score</p>,
                        <p className='classReport__cellCenter'>Progress</p>
                    ]}
                    columnTypes={[
                        'string',
                        ...Array(testData.classTestSections.length).fill('number'),
                        'number',
                        null
                      ]}
                    rows={testData.students.map(student => {
                        // const firstScoreIndex = student.testScores.findIndex(score => score !== null);
                        // const lastScoreIndex = student.testScores.findLastIndex(score => score !== null);
                        // const progress = 100*(student.testScores[lastScoreIndex]-student.testScores[firstScoreIndex])/student.testScores[firstScoreIndex]

                        return [
                            hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                            ...testData.classTestSections.map((subjectName) => (
                                (() => {
                                    const testSection = student.test?.testSections.find(
                                    (section) => section.subject === subjectName
                                    );
                                    return testSection?.score ? Math.floor(testSection.score) : null;
                                })()
                            )),
                            student.test.score ? Math.floor(student.test.score) : null,
                            <div className='classReport__progress classReport__cellCenter'>
                                {new Array(student.test.testModuleCount).fill(null).map((_count, c) => (
                                    <div className={student.test.completedModuleCount > c
                                        ? 'classReport__progressBar--filled'
                                        : (student.test.inProgress && c === 0)
                                        ? 'classReport__progressBar--started'
                                        : 'classReport__progressBar'
                                    }></div>
                                ))}
                            </div>
                        ];
                    })}
                    onSelectCell={testData.students.map(student => ([
                        hideStudentNames ? null : () => navigate(`/students/${student.studentId}`),
                        ...testData.classTestSections.map(() => null),
                        student.test.score? () => navigate(`/report/digital-sat/${student.test.testId}`): null,
                        null
                    ]))}
                    navigator={false}
                />
                <div className='classReport__scoreDistribution'>
                    <div className='classReport__scoreDistribution__header'>
                        <div className='classReport__scoreDistribution__title'>Score distribution</div>
                        <Dropdown4
                            width={190}
                            className='classReport__dropdown'
                            data={subjects}
                            onSelect={value => setSubjectIndex(subjects.findIndex(module => module === value))}
                            value={subjects[subjectIndex]}
                        />
                    </div>
                    <div className='classReport__scoreDistribution__content'>
                        <div className='classReport__scoreDistribution__graph'>
                            <ResponsiveContainer>
                                <BarChart data={scoreChartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                                    <XAxis
                                    dataKey="range"
                                    tick={{ fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    label={{
                                        value: 'Score',
                                        position: 'insideBottom',
                                        offset: -10,
                                        style: {
                                        fontSize: 12,
                                        fill: '#6B7280',
                                        fontWeight: 500,
                                        },
                                    }}
                                    />
                                    <YAxis
                                    allowDecimals={false}
                                    domain={[0, 'dataMax + 1']}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12 }}
                                    />
                                    <Tooltip />
                                    <Bar dataKey="students" fill="#4F46E5" radius={[4, 4, 0, 0]} minPointSize={2}/>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className='classReport__scoreDistribution__avgScore'>
                            <div className='classReport__scoreDistribution__avg'>Avg Score</div>
                            <div className='classReport__scoreDistribution__avgScoreNum'>{testData?.avgScore}</div>
                        </div>
                    </div>
                </div>
                <div className='classReport__category'>
                    <div className='classReport__scoreDistribution__title'>Category breakdown (%correct)</div>
                    <div className='classReport__scoreDistribution__analysisWrap'>
                        {Object.keys(testData.avgCorrectPercent).length > 0 ? (
                            Object.entries(testData.avgCorrectPercent).map(([subject, categories], index) => (
                                <div key={index} className='classReport__analysisBox'>
                                    <div className='classReport__analysisBoxTitle'>{subject}</div>
                                    <div className='classReport__analysisContentWrap'>
                                        {Object.entries(categories)?.map(([categoryName, answerNumInfo], i) => {
                                            const { numCorrect, numQuestions } = answerNumInfo;
                                            const percent = numQuestions > 0 ? (numCorrect / numQuestions) * 100 : 0;
                                            return (
                                                <div key={i} className='classReport__categoryWrap'>
                                                    <CategoryDetailLabel
                                                        name={categoryName}
                                                        categories={answerNumInfo.categories}
                                                        active={activeCategory === `${subject}|${categoryName}`}
                                                        onToggle={() => setActiveCategory(activeCategory === `${subject}|${categoryName}` ? null : `${subject}|${categoryName}`)}
                                                        onClose={() => setActiveCategory(null)}
                                                    />
                                                    <LevelBar className={subject==='Reading and Writing'
                                                                        ? 'LevelBar__greenBox'
                                                                        : 'LevelBar__orangeBox'
                                                                        } 
                                                                percent = {percent}/>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        ): (
                            <div className='classReport__analysisBox'>
                                <div className='classReport__analysisBoxTitle'>-</div>
                                <div className='classReport__analysisContentWrap'>
                                <div className='classReport__categoryWrap'>
                                    <div className='classReport__categoryTitle'>-</div>
                                    <LevelBar className='LevelBar__defaultBox' percent={0} />
                                </div>
                                </div>
                            </div>
                            )
                        }
                    </div>
                </div>
            </div>
            <div className='classReport__subHeaderRow'>
                <h2 className='classReport__subHeader'>
                    Student responses
                </h2>
                <Dropdown
                    className='classReport__dropdown'
                    data={modules}
                    onSelect={value => setModuleIndex(modules.findIndex(module => module === value))}
                    value={modules[moduleIndex]}
                />
                <div className='classReport__buttonGroup'>
                    {/* <Button className='classReport__button' onClick={printBatchReport}>
                        {printInProgress ? <img src={Spinner} /> : 'Print'}
                    </Button> */}
                    <Button className='classReport__button' onClick={exportStudentTestData}>
                        {downloadInProgress ? <img src={Spinner} /> : 'Download'}
                    </Button>
                </div>
            </div>
            <Table
                header={['Students', ...testData.modules[moduleIndex].questions.map((question, q) => (
                    // <p className={`
                    //     classReport__cellCenter
                    //     classReport__response 
                    //     classReport__cursor
                    //     ${testData.students.filter(student => (
                    //         student.moduleAnswers.find(moduleAnswer => (
                    //             moduleAnswer.subject === testData.modules[moduleIndex].subject
                    //             && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                    //             && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                    //         ))
                    //     )).find(student => (
                    //         !student.moduleAnswers.find(moduleAnswer => (
                    //             moduleAnswer.subject === testData.modules[moduleIndex].subject
                    //             && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                    //             && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                    //         )).answers[q].isCorrect
                    //     )) && 'classReport__cellWrong'}
                    // `}
                    <p className={`
                        classReport__cellCenter
                        classReport__response 
                        classReport__cursor
                        classReport__tableHeader
                    `}
                    onClick={() => handleQuestionPopup(question[1])}>
                        Q{question[0]}
                    </p>
                ))]}
                rows={[
                    [
                        <p className='classReport__header'># Incorrect</p>,
                        ...testData.modules[moduleIndex].questions.map((_question, q) => (
                            <p className='classReport__header classReport__cellCenter'>
                                {testData.students.filter(student => (
                                    student.moduleAnswers.find(moduleAnswer => (
                                        moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                        && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                                    ))
                                )).filter(student => (
                                    !student.moduleAnswers.find(moduleAnswer => (
                                        moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                        && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                                    )).answers[q].isCorrect
                                )).length
                            }</p>
                        ))
                    ],
                    ...testData.students.filter(student => (
                        student.moduleAnswers.find(moduleAnswer => (
                            moduleAnswer.subject === testData.modules[moduleIndex].subject
                            && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                            && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                        ))
                    )).map(student => ([
                        hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                        ...student.moduleAnswers.find(moduleAnswer => (
                            moduleAnswer.subject === testData.modules[moduleIndex].subject
                            && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                            && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                        )).answers.map(answer => (
                            <p className={`classReport__cellCenter classReport__response ${!answer.isCorrect && (answer.answer
                                ? 'classReport__cellWrong'
                                : 'classReport__cellSkipped'
                            )}`}>
                                {answer.answer ? answer.answer : '-'}
                            </p>
                        ))
                    ])),
                ]}
                navigator={false}
            />
            {reportData &&
                <div className='classReport__report' ref={printRef}>
                    {reportData.map(testData => (<>
                        <div className='classReportPrint__headerGroup'>
                            <div className='classReportPrint__headerSection'>
                                {testData.logo && 
                                    <img
                                        className='classReportPrint__headerLogo'
                                        alt='logo'
                                        src={testData.logo}
                                    />
                                }
                            </div>
                            <div className='classReportPrint__headerSection'>
                                <div className='classReportPrint__headerInfo'>
                                    <p>
                                        Name: {testData.studentName}
                                    </p>
                                    <p>
                                        Email: {testData.email}
                                    </p>
                                    <p>
                                        Test name: {testData.name}
                                    </p>
                                    <p>
                                        Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        {/* <div className='classReportPrint__headerGroup'>
                            <h1 className='classReportPrint__headerTitle'>
                                Test Result Overview
                            </h1>
                            <div className='classReportPrint__buttonGroup'>
                                <Button className='classReportPrint__button' secondary onClick={handlePrint}>
                                    PRINT REPORT
                                </Button>
                                <ButtonLink className='classReportPrint__button' to={`/review/${testId}`}>
                                    REVIEW QUESTIONS
                                </ButtonLink>
                            </div>
                        </div> */}
                        <div className='classReportPrint__card'>
                            <div className='classReportPrint__cardRow'>
                                <div className='classReportPrint__scoresGroup'>
                                    <h2 className='classReportPrint__scoreTitle'>TOTAL SCORE</h2>
                                    <ScoreWithScale
                                        className='classReportPrint__scoreWithScale'
                                        score={Math.floor(testData.score)}
                                        scoreMin={Math.floor(testData.minScore)}
                                        scoreMax={Math.floor(testData.maxScore)}
                                        fontSize={70}
                                        // fontSize={windowSize.width > 576
                                        //     ? 100
                                        //     : 70
                                        // }
                                    />
                                    <div className='classReportPrint__scoreDivider'></div>
                                    <h2 className='classReportPrint__scoreTitle'>SECTION SCORES</h2>
                                    {testData.testSections.map((testSection, t) => (<>
                                        <h3 className='classReportPrint__sectionScoreTitle'>
                                            {testSection.subject}
                                        </h3>
                                        <ScoreWithScale
                                            className='classReportPrint__scoreWithScale'
                                            score={Math.floor(testSection.score)}
                                            scoreMin={Math.floor(testSection.minScore)}
                                            scoreMax={Math.floor(testSection.maxScore)}
                                        />
                                        {t < testData.testSections.length - 1 &&
                                            <div className='classReportPrint__scoreDivider'></div>
                                        }
                                    </>))}
                                </div>
                                <div className='classReportPrint__overview'>
                                    <h2 className='classReportPrint__cardSubTitle'>
                                        Raw Scores
                                    </h2>
                                    {testData?.testType==='Digital ACT'?
                                        <div className='classReportPrint__overviewRowScores'>
                                            <table className='classReportPrint__overviewTable'>
                                                <tbody>
                                                    <tr>
                                                        <th></th>
                                                        {testData.testSections.map((testSection, index) => (
                                                            <td key={`subject-${index}`}>{testSection.subject.replace('ACT ','')}</td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th>Correct</th>
                                                        {testData.testSections.map((testSection, index) => {
                                                            const testModule = testData.testModules.find(module => module.subject === testSection.subject);
                                                            return testModule ? (
                                                                <td key={`correct-${index}`}>
                                                                    {testModule.answers.filter(answer => answer.isCorrect).length}/{testModule.answers.length}
                                                                </td>
                                                            ) : <td key={`correct-${index}`}>-</td>;
                                                        })}
                                                    </tr>
                                                    <tr>
                                                        <th>Time</th>
                                                        {testData.testSections.map((testSection, index) => {
                                                            const testModule = testData.testModules.find(module => module.subject === testSection.subject);
                                                            return testModule ? (
                                                                <td key={`time-${index}`}>
                                                                    {formatTime(testModule.answers.reduce((total, answer) => 
                                                                        total + answer.timeSpent, 0))}
                                                                </td>
                                                            ) : <td key={`time-${index}`}>-</td>;
                                                        })}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    :
                                        <div className='classReportPrint__overviewRow'>
                                            {testData.testSections.map(testSection => (
                                                <div className='classReportPrint__overviewItem'>
                                                    <h4 className='classReportPrint__overviewLabel'>
                                                        {testSection.subject}
                                                    </h4>
                                                    <table className='classReportPrint__overviewTable'>
                                                        <tbody>
                                                            <tr>
                                                                <th></th>
                                                                <th>Correct</th>
                                                                <th>Time</th>
                                                            </tr>
                                                            {testData.testModules.filter(testModule => (
                                                                testModule.subject === testSection.subject
                                                            )).map((testModule, t) => (
                                                                <tr>
                                                                    <td>
                                                                        Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                                                                    </td>
                                                                    <td>{testModule.answers.filter(answer => answer.isCorrect).length}/{testModule.answers.length}</td>
                                                                    <td>{formatTime(testModule.answers.reduce((total, answer) => (
                                                                        total + answer.timeSpent
                                                                    ), 0))}</td>
                                                                </tr>
                                                            ))}
                                                            <tr>
                                                                <td>Total</td>
                                                                <td>{testData.testModules.filter(testModule => (
                                                                    testModule.subject === testSection.subject
                                                                )).reduce((total, testModule) => (
                                                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                                                ), 0)}/{testData.testModules.filter(testModule => (
                                                                    testModule.subject === testSection.subject
                                                                )).reduce((total, testModule) => (
                                                                    total + testModule.answers.length
                                                                ), 0)}</td>
                                                                <td>{formatTime(testData.testModules.filter(testModule => (
                                                                    testModule.subject === testSection.subject
                                                                )).reduce((total, testModule) => (
                                                                    total + testModule.answers.reduce((time, answer) => (
                                                                        time + answer.timeSpent
                                                                    ), 0)
                                                                ), 0))}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                    <h2 className='classReportPrint__cardSubTitle'>
                                        Category Scores
                                    </h2>
                                    <div className='classReportPrint__overviewRow'>
                                        {testData.testSections.map((testSection, s) => (
                                            <div className='classReportPrint__overviewItem'>
                                                <h4 className='classReportPrint__overviewLabel'>
                                                    {testSection.subject.replace('ACT ', '')}
                                                </h4>
                                                {testSection.rootCategories.map(rootCategory => (<>
                                                    <h5 className='classReportPrint__levelLabel'>
                                                        {rootCategory.name}
                                                    </h5>
                                                    <LevelBar
                                                        className={s % 2 === 0 ? 'classReportPrint__levelGreen' : 'classReportPrint__levelOrange'}
                                                        percent={100*rootCategory.numCorrect/rootCategory.numQuestions}
                                                        label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                                    />
                                                </>))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <h2 className='classReportPrint__cardSubTitle--centered'>
                                Section Time Averages
                            </h2>
                            <div className='classReportPrint__overviewRow'>
                                {testData.testSections.map(testSection => (
                                    <div className='classReportPrint__overviewItem'>
                                        <h4 className='classReportPrint__overviewLabel--centered'>
                                            {testSection.subject.replace('ACT ', '')}
                                        </h4>
                                        <table className='classReportPrint__timeTable'>
                                            <tbody>
                                                <tr>
                                                    <th></th>
                                                    <th>Section Time</th>
                                                    <th>Time Per Question</th>
                                                </tr>
                                                {testSection.rootCategories.map(rootCategory => (
                                                    <tr>
                                                        <td>
                                                            {rootCategory.name}
                                                        </td>
                                                        <td>
                                                            {formatTime2(rootCategory.timeSpent)}
                                                        </td>
                                                        <td>
                                                            <GaugeComponent
                                                                className='classReportPrint__timeGauge'
                                                                type='semicircle'
                                                                value={rootCategory.timeSpent/rootCategory.numQuestions}
                                                                minValue={0}
                                                                maxValue={testSection.subject === 'Reading and Writing'
                                                                    ?   timePerQuestionRW * 3
                                                                    :   testSection.subject === 'Math'
                                                                    ?   timePerQuestionMath * 3
                                                                    :   180
                                                                }
                                                                arc={{
                                                                    'subArcs': [
                                                                        { length: 0.33, color: "#5BE12C"},
                                                                        { length: 0.33, color: "#F5CD19"},
                                                                        { length: 0.33, color: "#EA4228"}
                                                                    ]
                                                                }}
                                                                // style={{ 'width': '200px' }}
                                                                pointer={{
                                                                    'width': 10,
                                                                }}
                                                                labels={{
                                                                    'valueLabel': {
                                                                        // 'formatTextValue': (value) => formatTime(value),
                                                                        // 'style': {
                                                                        //     'fontSize': '40px',
                                                                        //     'fill': '#000',
                                                                        //     'textShadow': null,
                                                                        //     'marginTop': 20
                                                                        // }
                                                                        'hide': true
                                                                    },
                                                                    'tickLabels': {
                                                                        'hideMinMax': true
                                                                    }
                                                                }}
                                                            />
                                                            {formatTime2(rootCategory.timeSpent/rootCategory.numQuestions)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className='classReportPrint__printBreak'></div>
                        <div className='classReportPrint__categoryPrint'>
                            {testData.testSections.map(testSection => <div className='classReportPrint__categoryPrintSection'>
                                <h3 className='classReportPrint__categoryPrintSubjectName'>{testSection.subject}</h3>
                                <div className='classReportPrint__categoryPrintSubject'>
                                    {testSection.rootCategories.map(rootCategory => <div className='classReportPrint__categoryPrintRootGroup'>
                                        <h4 className='classReportPrint__categoryPrintRoot'>{rootCategory.name}</h4>
                                        {rootCategory.categories.map(category => (
                                            <div className='classReportPrint__levelDetailedItem'>
                                                <p>{category.name}</p>
                                                <p className={category.numCorrect/category.numQuestions >= 0.8
                                                    ? 'classReportPrint__levelDetailedGreen'
                                                    : category.numCorrect/category.numQuestions >= 0.4
                                                    ? 'classReportPrint__levelDetailedYellow'
                                                    : 'classReportPrint__levelDetailedRed'
                                                }>
                                                    {category.numCorrect}/{category.numQuestions}
                                                </p>
                                            </div>
                                        ))}
                                    </div>)}
                                </div>
                            </div>)}
                        </div>
                    </>))}
                </div>
            }
        </div>
    );
}

export default ClassSATReport;